"use client";

import Link from "next/link";
import { ArrowRight, ShieldCheck } from "lucide-react";

interface ScanCtaBannerProps {
  /** e.g. "rental agreement" or "property documents" */
  documentType?: string;
}

/**
 * Inline banner CTA (used inside content flow on mobile / fallback).
 */
export function ScanCtaBanner({ documentType = "contract" }: ScanCtaBannerProps) {
  return (
    <div className="bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-950/30 dark:to-orange-950/20 border border-red-200/50 dark:border-red-800/30 rounded-lg p-5 sm:p-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center">
          <ShieldCheck className="h-5 w-5 text-red-600 dark:text-red-400" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
            Got a {documentType}? Scan it for red flags.
          </h3>
          <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
            Upload your {documentType} and get a clause-by-clause risk analysis verified against Indian law. Free — no signup needed.
          </p>
        </div>
        <Link
          href="/try"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors shrink-0"
        >
          Free scan
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </div>
  );
}

/**
 * Sticky sidebar CTA — 3:4 ratio card matching the blog sidebar style.
 * Uses the brand green theme for consistency across blog and calculator pages.
 */
export function ScanCtaSidebar({ documentType = "contract" }: ScanCtaBannerProps) {
  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-brand/20 bg-gradient-to-br from-brand/5 via-brand/10 to-brand/5"
      style={{ aspectRatio: "3/4" }}
    >
      <div className="flex h-full flex-col items-center justify-center p-6 text-center">
        {/* Icon */}
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-brand/15">
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-brand"
          >
            <path d="M9 12l2 2 4-4" />
            <path d="M12 3c1.2 0 2.4.6 3 1.5A3.5 3.5 0 0 1 19 8v1a2 2 0 0 1 0 4v1a3.5 3.5 0 0 1-4 3.5c-.6.9-1.8 1.5-3 1.5s-2.4-.6-3-1.5A3.5 3.5 0 0 1 5 14v-1a2 2 0 0 1 0-4V8a3.5 3.5 0 0 1 4-3.5C9.6 3.6 10.8 3 12 3" />
          </svg>
        </div>

        <h3 className="text-lg font-bold text-foreground leading-tight">
          Got a {documentType}?
        </h3>
        <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
          Upload your document and get an AI-powered clause-by-clause risk analysis in minutes.
        </p>

        {/* Feature list */}
        <ul className="mt-4 space-y-2 text-left text-xs text-muted-foreground">
          <li className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-brand flex-shrink-0" />
            Indian law verified
          </li>
          <li className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-brand flex-shrink-0" />
            Red / amber / green flags
          </li>
          <li className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-brand flex-shrink-0" />
            First 2 scans free
          </li>
        </ul>

        <Link
          href="/try"
          className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brand px-5 py-3 text-sm font-semibold text-foreground shadow-lg shadow-brand/20 transition hover:bg-brand/90 hover:shadow-brand/30"
        >
          Scan Now — Free
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </Link>
      </div>
    </div>
  );
}
